/**
 * PLT-7912: what each tool call in an assistant turn reads as in the turn's tool trail — the one
 * gray line that lists, when opened, every tool the turn used (see `tool-trail.tsx`).
 *
 * Plain data, no React: a step is described from the tool-call part alone, so the trail can draw a
 * row for a tool that has no UI of its own.
 */
import { friendlyImpactTitle } from '@upright/common/utils';
import { friendlySustainabilityMatterTitle } from '@upright/common/model/input/materiality-assessment/matters';
import type { SustainabilityMatterId } from '@upright/common/model/input/materiality-assessment/types-matters';

/**
 * Tool calls that are not steps: the synthetic error, notice and limit surfaces, and the tools that
 * draw part of the answer itself (follow-up chips, KPI tiles, callouts). They render where they are.
 */
const NOT_STEPS: ReadonlySet<string> = new Set([
  'chat_error',
  'chat_notice',
  'ai_chat_token_limit',
  'suggest_followups',
  'present_headline_kpis',
  'emit_callout',
]);

/** The synthetic part the adapter builds from the model's thinking blocks (`upright-chat-adapter.ts`). */
export const REASONING_TOOL_NAME = 'reasoning_summary';

/** Steps that are minor beside the rest of a turn: the summary names them only when little else ran. */
const LESSER_STEPS: ReadonlySet<string> = new Set(['search', 'web_fetch']);

/**
 * Marks the result of a step a reopened chat rebuilt from the call alone (`tool-step` in the
 * persisted content events): the trail draws its row, and nothing else may treat it as a result.
 * `stripLargeToolData` drops these parts from the history sent back to the model.
 */
const TRAIL_ONLY_KEY = '_trailOnly';

export function trailOnlyResult(failed: boolean): Record<string, unknown> {
  return { data: failed ? { error: 'failed' } : {}, [TRAIL_ONLY_KEY]: true };
}

export function isTrailOnlyResult(result: unknown): boolean {
  return !!result && typeof result === 'object' && (result as Record<string, unknown>)[TRAIL_ONLY_KEY] === true;
}

/** The tool-call part fields a step is read from. */
export interface TrailPart {
  toolName: string;
  toolCallId: string;
  args: Record<string, unknown>;
  result?: unknown;
}

/** Whether calls of this tool are steps of the trail rather than part of the answer. */
export function isTrailToolName(toolName: string): boolean {
  return !NOT_STEPS.has(toolName);
}

export function isTrailPart(part: { type: string; toolName?: string }): boolean {
  return part.type === 'tool-call' && typeof part.toolName === 'string' && isTrailToolName(part.toolName);
}

export type TrailIcon =
  | 'search'
  | 'web'
  | 'page'
  | 'knowledge'
  | 'data'
  | 'list'
  | 'proposal'
  | 'code'
  | 'climate'
  | 'company'
  | 'financial'
  | 'reasoning'
  | 'generic';

/** What a row can open under itself. */
export type TrailDetail = 'card' | 'code' | 'sources' | 'reasoning';

export interface TrailStep {
  /** The tool call the row is for; the reasoning row takes its first block's id. */
  toolCallId: string;
  toolName: string;
  icon: TrailIcon;
  /** The verb phrase: "Read DMA results", "Searched the web". */
  label: string;
  /** Gray text after the label: the company, the query, the matter. */
  subject: string | null;
  state: 'running' | 'done' | 'failed';
  /** The collapsed line's text while this step runs: the status line the tool showed before PLT-7912. */
  liveText: string;
  detail: TrailDetail | null;
}

interface StepCopy {
  icon: TrailIcon;
  running: string;
  done: string;
  failed: string;
  /** Gray text after the label, from the call's arguments and, once it is in, its result data. */
  subject?: (args: Record<string, unknown>, data: Record<string, unknown> | undefined) => string | null;
  /** The running text, when it is not the running verb and the subject. */
  live?: (args: Record<string, unknown>) => string;
  /** Overrides the done label from the result data. */
  doneFrom?: (data: Record<string, unknown>) => string;
  detail?: TrailDetail;
  /** How long the step reads as running at least, once it was seen running (see `minRunningMs`). */
  minRunningMs?: number;
}

function read(noun: string, icon: TrailIcon = 'data', extra: Partial<StepCopy> = {}): StepCopy {
  return { icon, running: `Reading ${noun}`, done: `Read ${noun}`, failed: `Couldn't read ${noun}`, ...extra };
}

function act(running: string, done: string, failed: string, icon: TrailIcon, extra: Partial<StepCopy> = {}): StepCopy {
  return { icon, running, done, failed, ...extra };
}

/**
 * Helpers that turn a tool's args into a context-specific suffix for card titles and trail rows.
 * Each returns `null` when the arg is missing or unrecognised, so the bare label is rendered — the
 * suffix is cosmetic and never the difference between "the card works" and "the card breaks".
 * (PLT-4625 follow-up issue 5.)
 */
export function impactCategorySuffix(args: Record<string, unknown>): string | null {
  const code =
    typeof args.category === 'string'
      ? args.category
      : typeof args.impact === 'string'
        ? args.impact
        : typeof args.impactId === 'string'
          ? args.impactId
          : null;
  if (code === null) return null;
  // `friendlyImpactTitle` echoes the raw code when unknown; keep that
  // fallback (e.g. "E4" → "Biodiversity", "ZZZ" → "ZZZ") so the
  // suffix at least communicates *which* call is which when the LLM
  // passes an out-of-vocab code.
  return friendlyImpactTitle(code);
}

export function matterSuffix(args: Record<string, unknown>): string | null {
  const id = typeof args.matterId === 'string' ? args.matterId : null;
  if (id === null) return null;
  try {
    // `friendlySustainabilityMatterTitle` throws for unknown ids via
    // `getMatterById`. Catch and fall through so a bad id never
    // crashes the chat surface — the label degrades to "Single
    // matter: <raw id>" which is still informative.
    return friendlySustainabilityMatterTitle(id as SustainabilityMatterId);
  } catch {
    return id;
  }
}

/** The impact a product impact reasoning call is about: a Net Impact code, or a DMA matter in the same slot. */
export function impactReasoningSuffix(args: Record<string, unknown>): string | null {
  return impactCategorySuffix(args) ?? matterSuffix({ matterId: args.impact });
}

/** Summarise the filters applied in a screen_companies call. */
function summariseScreenFilters(args: Record<string, unknown>): string {
  const parts: string[] = [];
  const push = (label: string, val: unknown) => {
    if (val == null) return;
    if (Array.isArray(val)) {
      if (val.length === 0) return;
      parts.push(`${label}: ${val.join(', ')}`);
    } else if (typeof val === 'string' && val.length > 0) {
      parts.push(`${label}: ${val}`);
    } else if (typeof val === 'number') {
      parts.push(`${label}: ${val}`);
    }
  };
  push('country', args.country);
  push('continent', args.continent);
  push('industry', args.industry);
  push('listing', args.listingStatus);
  push('set', args.predefinedSet);
  push('most positive', args.mostPositiveImpactCategory);
  push('most negative', args.mostNegativeImpactCategory);
  if (typeof args.sortBy === 'string') parts.push(`sort: ${args.sortBy}`);
  return parts.join(', ');
}

/** The locations a climate risk over time call is for, as its card title names them. */
export function trajectoryLocationsSuffix(args: Record<string, unknown>): string | null {
  const locations = Array.isArray(args.locations) ? (args.locations as Array<{ label?: unknown }>) : [];
  const labels = locations.map(location => location.label).filter((l): l is string => typeof l === 'string' && !!l);
  if (labels.length === 0) return null;
  return labels.length <= 2 ? labels.join(', ') : `${labels[0]} and ${labels.length - 1} more`;
}

function stringArg(name: string) {
  return (args: Record<string, unknown>) => {
    const value = args[name];
    return typeof value === 'string' && value.length > 0 ? value : null;
  };
}

function quotedArg(name: string) {
  return (args: Record<string, unknown>) => {
    const value = stringArg(name)(args);
    return value ? `"${value}"` : null;
  };
}

function dataString(data: Record<string, unknown> | undefined, name: string): string | null {
  const value = data?.[name];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

// A save before the company's first results is saved too: those results will use it (PLT-7919).
const SAVED_PUBLISH_STATUSES: ReadonlySet<string> = new Set([
  'published',
  'saved-before-first-results',
  'recompute-failed',
]);

const STEP_COPY: Partial<Record<string, StepCopy>> = {
  web_search: act('Searching the web', 'Searched the web', "Couldn't search the web", 'web', {
    subject: stringArg('query'),
    live: args => (typeof args.query === 'string' ? `Web search: ${args.query}` : 'Searching the web…'),
    detail: 'sources',
  }),
  web_fetch: read('web page', 'page', { subject: stringArg('url') }),
  search_knowledge_base: act(
    'Searching the knowledge base',
    'Searched the knowledge base',
    "Couldn't search the knowledge base",
    'knowledge',
    {
      subject: quotedArg('query'),
      live: args => {
        const query = quotedArg('query')(args);
        return query ? `Searching knowledge base: ${query}…` : 'Searching knowledge base…';
      },
    },
  ),
  screen_companies: act('Screening companies', 'Screened companies', "Couldn't screen companies", 'list', {
    subject: (args, data) => {
      const filters = summariseScreenFilters(args);
      const total = data?.totalMatches;
      const count = typeof total === 'number' ? `${total} match${total === 1 ? '' : 'es'}` : null;
      return [count, filters].filter(Boolean).join(' · ') || null;
    },
    live: args => {
      const filters = summariseScreenFilters(args);
      return `Screening companies${filters ? ` (${filters})` : ''}…`;
    },
  }),
  get_net_impact_summary: read('net impact'),
  get_contributing_products: read('contributing products', 'data', { subject: impactCategorySuffix }),
  get_dma_results: read('DMA results'),
  get_dma_materiality_matrix: read('materiality matrix'),
  get_single_matter_summary: read('matter summary', 'data', { subject: matterSuffix }),
  get_product_impact_reasoning: read('impact reasoning', 'data', { subject: impactReasoningSuffix }),
  get_supplier_risk_assessment: read('supplier risk'),
  get_supplier_base_report: read('supplier base', 'data', {
    subject: (_args, data) => dataString(data, 'buyerCompanyName'),
  }),
  get_investor_report: read('financial risk and value creation report'),
  get_supporting_articles: read('supporting articles', 'page', { subject: stringArg('productCode') }),
  get_financial_effects_summary: read('financial effects', 'financial'),
  get_product_value_chain: read('product value chain', 'data', {
    subject: args => [stringArg('productCode')(args), stringArg('direction')(args)].filter(Boolean).join(' · ') || null,
  }),
  get_sdg_alignment: read('SDG alignment'),
  get_eu_taxonomy_summary: read('EU Taxonomy alignment'),
  get_sfdr_pais_summary: read('SFDR PAIs'),
  get_multi_company_net_impact_summary: act(
    'Comparing net impact',
    'Compared net impact',
    "Couldn't compare net impact",
    'data',
  ),
  get_multi_company_dma_summary: act(
    'Comparing DMA results',
    'Compared DMA results',
    "Couldn't compare DMA results",
    'data',
  ),
  get_group_exposure: read('portfolio exposure'),
  create_dd_company: act('Creating company', 'Created company', "Couldn't create company", 'company'),
  create_supplier: act('Adding supplier', 'Added supplier', "Couldn't add supplier", 'company'),
  get_dd_company_status: read('company status', 'company'),
  get_dd_product_set: read('product set', 'list'),
  publish_dd_product_set: act(
    'Publishing product set',
    'Published product set',
    "Couldn't publish product set",
    'list',
  ),
  update_dd_product_set: act('Updating product set', 'Updated product set', "Couldn't update product set", 'list'),
  save_dd_product_set_from_files: act('Saving product set', 'Saved product set', "Couldn't save product set", 'list'),
  review_staged_fund: act('Preparing fund review', 'Prepared fund review', "Couldn't prepare fund review", 'list'),
  review_fund_version: act(
    'Preparing fund version review',
    'Prepared fund version review',
    "Couldn't prepare fund version review",
    'list',
  ),
  offer_financial_statements: act(
    'Preparing financial statement options',
    'Prepared financial statement options',
    "Couldn't prepare financial statement options",
    'financial',
  ),
  extract_financial_statements: act(
    'Extracting financial statements',
    'Extracted financial statements',
    "Couldn't extract financial statements",
    'financial',
  ),
  revise_draft: act(
    'Revising financial statement draft',
    'Revised financial statement draft',
    "Couldn't revise financial statement draft",
    'financial',
  ),
  publish_draft: act(
    'Saving financial statements',
    'Saved financial statements',
    "Couldn't save financial statements",
    'financial',
    {
      doneFrom: data =>
        typeof data.status === 'string' && SAVED_PUBLISH_STATUSES.has(data.status)
          ? 'Saved financial statements'
          : 'Financial statements not saved yet',
    },
  ),
  compare_releases: act('Comparing releases', 'Compared releases', "Couldn't compare releases", 'data', {
    subject: (args, data) => {
      const releases =
        typeof args.releaseA === 'string' && typeof args.releaseB === 'string'
          ? `${args.releaseA} → ${args.releaseB}`
          : null;
      return [releases, dataString(data, 'companyName')].filter(Boolean).join(' · ') || null;
    },
  }),
  code_execution: act('Running code', 'Ran code', "Couldn't run code", 'code', { detail: 'code' }),
  // The code sandbox (`code_execution_20250825`) runs code as shell commands; its file tool is `fileStepCopy`.
  bash_code_execution: act('Running code', 'Ran code', "Couldn't run code", 'code', { detail: 'code' }),
  execute_python: act('Running code', 'Ran code', "Couldn't run code", 'code', { detail: 'code' }),
  get_style_guide: read('style guide', 'page'),
  get_company_physical_climate_risk: read('physical climate risk', 'climate', {
    live: () => 'Identifying company sites and assessing physical climate risk…',
  }),
  identify_company_sites: act(
    'Identifying company sites',
    'Identified company sites',
    "Couldn't identify company sites",
    'company',
    {
      subject: stringArg('companyName'),
      live: args =>
        typeof args.companyName === 'string'
          ? `Identifying sites for ${args.companyName}…`
          : 'Identifying company sites…',
    },
  ),
  get_climate_risk_grid: read('climate risk map', 'climate', {
    subject: args => (typeof args.hazard === 'string' ? args.hazard.replace(/_/g, ' ') : null),
  }),
  get_climate_risk_trajectory: read('climate risk over time', 'climate', { subject: trajectoryLocationsSuffix }),
  get_location_climate_risk: act(
    'Assessing location climate risk',
    'Assessed location climate risk',
    "Couldn't assess location climate risk",
    'climate',
  ),
  // PLT-5214: the calculation is effectively synchronous, so without a floor it flips to done within
  // milliseconds, fast enough that the result reads as made up rather than calculated. Cosmetic: it
  // never delays the answer.
  compute_financial_effects: act(
    'Calculating financial effects',
    'Calculated financial effects',
    "Couldn't calculate financial effects",
    'financial',
    { minRunningMs: 3000 },
  ),
  validate_financial_effects_inputs: act(
    'Validating financial effects inputs',
    'Validated financial effects inputs',
    "Couldn't validate financial effects inputs",
    'financial',
  ),
  propose_custom_framework_themes: act(
    'Proposing framework themes',
    'Proposed framework themes',
    "Couldn't propose framework themes",
    'proposal',
  ),
  create_custom_impact_framework: act(
    'Creating custom framework',
    'Created custom framework',
    "Couldn't create custom framework",
    'proposal',
  ),
  edit_custom_impact_framework: act(
    'Editing custom framework',
    'Edited custom framework',
    "Couldn't edit custom framework",
    'proposal',
  ),
  mark_custom_impact_framework_ready: act(
    'Marking custom framework ready',
    'Marked custom framework ready',
    "Couldn't mark custom framework ready",
    'proposal',
  ),
  restore_custom_impact_framework_version: act(
    'Restoring framework version',
    'Restored framework version',
    "Couldn't restore framework version",
    'proposal',
  ),
  get_custom_impact_framework: read('custom framework', 'list'),
  // The tools behind the action cards. Their cards stay in the message; the row says they ran.
  propose_tailoring_batch: act(
    'Preparing tailoring proposal',
    'Prepared tailoring proposal',
    "Couldn't prepare tailoring proposal",
    'proposal',
  ),
  propose_operational_data_batch: act(
    'Preparing operational-data proposal',
    'Prepared operational-data proposal',
    "Couldn't prepare operational-data proposal",
    'proposal',
  ),
  propose_sustainability_indicators_batch: act(
    'Preparing sustainability-indicators proposal',
    'Prepared sustainability-indicators proposal',
    "Couldn't prepare sustainability-indicators proposal",
    'proposal',
  ),
  request_tailoring_selection: act(
    'Preparing tailoring selection',
    'Prepared tailoring selection',
    "Couldn't prepare tailoring selection",
    'proposal',
  ),
  request_own_insights_start: act(
    'Checking own insights',
    'Prepared own insights proposal',
    "Couldn't check own insights",
    'proposal',
  ),
  propose_site_locations: act(
    'Preparing site locations',
    'Prepared site locations',
    "Couldn't prepare site locations",
    'proposal',
  ),
  review_supplier_import: act(
    'Preparing the supplier list review',
    'Prepared the supplier list review',
    "Couldn't prepare the supplier list review",
    'proposal',
  ),
  request_upright_review: act(
    'Preparing review request',
    'Prepared review request',
    "Couldn't prepare review request",
    'proposal',
  ),
  propose_tailoring_removal: act(
    'Preparing tailoring removal',
    'Prepared tailoring removal',
    "Couldn't prepare tailoring removal",
    'proposal',
  ),
};

function fileName(args: Record<string, unknown>): string | null {
  return typeof args.path === 'string' && args.path.length > 0 ? (args.path.split('/').pop() ?? null) : null;
}

/** The code sandbox's file tool, by what it did to the file. */
const FILE_STEP_COPY: Partial<Record<string, StepCopy>> = {
  view: act('Reading a file', 'Read a file', "Couldn't read a file", 'code', { subject: fileName, detail: 'code' }),
  create: act('Writing a file', 'Wrote a file', "Couldn't write a file", 'code', { subject: fileName, detail: 'code' }),
  str_replace: act('Editing a file', 'Edited a file', "Couldn't edit a file", 'code', {
    subject: fileName,
    detail: 'code',
  }),
};

/** The file tool's command streams in after the call starts, so a call can still be without one. */
function fileStepCopy(args: Record<string, unknown>): StepCopy {
  const copy = typeof args.command === 'string' ? FILE_STEP_COPY[args.command] : undefined;
  return (
    copy ??
    act('Working on a file', 'Worked on a file', "Couldn't work on a file", 'code', {
      subject: fileName,
      detail: 'code',
    })
  );
}

/** The code a code step ran or wrote, as its row shows under "Show code". */
export function codeOf(toolName: string, args: Record<string, unknown>): string {
  const text = (value: unknown) => (typeof value === 'string' ? value : '');
  switch (toolName) {
    case 'bash_code_execution':
      return text(args.command);
    case 'text_editor_code_execution':
      return text(args.file_text) || text(args.new_str);
    default:
      return text(args.code);
  }
}

const ACRONYMS: Record<string, string> = {
  dma: 'DMA',
  dd: 'DD',
  eu: 'EU',
  sdg: 'SDG',
  sdgtarget: 'SDG target',
  sfdr: 'SFDR',
  pais: 'PAIs',
  iros: 'IROs',
  kpis: 'KPIs',
  faq: 'FAQ',
};

/** Verb forms for the first word of a tool name: [running, done, failed]. */
const IRREGULAR_VERBS: Partial<Record<string, [string, string, string]>> = {
  get: ['Reading', 'Read', "Couldn't read"],
  find: ['Finding', 'Found', "Couldn't find"],
  set: ['Setting', 'Set', "Couldn't set"],
};

function verbForms(verb: string): [string, string, string] {
  const irregular = IRREGULAR_VERBS[verb];
  if (irregular) return irregular;
  const capitalised = verb.charAt(0).toUpperCase() + verb.slice(1);
  const failed = `Couldn't ${verb}`;
  if (verb.endsWith('e')) return [`${capitalised.slice(0, -1)}ing`, `${capitalised}d`, failed];
  if (/[^aeiou]y$/.test(verb)) return [`${capitalised}ing`, `${capitalised.slice(0, -1)}ied`, failed];
  return [`${capitalised}ing`, `${capitalised}ed`, failed];
}

/**
 * Copy for a tool with no entry above — every tool the agent can call that has no UI of its own
 * (`get_company_overview`, `list_dma_matters`, …). `get_company_overview` reads "Read company
 * overview": the first word is the verb, the rest the object.
 */
function genericCopy(toolName: string): StepCopy {
  const [verb = toolName, ...rest] = toolName.split('_');
  const object = rest.map(word => ACRONYMS[word] ?? word).join(' ');
  const [running, done, failed] = verbForms(verb);
  const withObject = (phrase: string) => (object ? `${phrase} ${object}` : phrase);
  return {
    icon: verb === 'search' ? 'search' : verb === 'list' ? 'list' : verb === 'get' ? 'data' : 'generic',
    running: withObject(running),
    done: withObject(done),
    failed: withObject(failed),
  };
}

function resultData(result: unknown): Record<string, unknown> | undefined {
  if (!result || typeof result !== 'object') return undefined;
  const data = (result as { data?: unknown }).data ?? result;
  return typeof data === 'object' ? (data as Record<string, unknown>) : undefined;
}

/**
 * A call that failed: the generic tool-failure envelope (`{error: '…'}`), a usage limit, or a
 * card call the server refused. A no-data answer (`dataAvailable: false`) is not a failure — the
 * tool ran and the model explains what it found.
 */
function isFailedResult(data: Record<string, unknown> | undefined): boolean {
  return data !== undefined && data.error !== undefined && data.error !== null;
}

type StepState = TrailStep['state'];

/** The search row: the company the search settled on, or what was searched for. */
function describeSearch(
  part: TrailPart,
  data: Record<string, unknown> | undefined,
  state: StepState,
): Omit<TrailStep, 'toolCallId' | 'toolName'> {
  const query = typeof part.args.query === 'string' && part.args.query.length > 0 ? part.args.query : null;
  const liveText = query ? `Searching for "${query}"…` : 'Searching…';
  if (state === 'running') {
    return { icon: 'search', label: 'Searching companies', subject: query, state, liveText, detail: null };
  }
  if (state === 'failed') {
    return {
      icon: 'search',
      label: "Couldn't search companies",
      subject: query,
      state: 'failed',
      liveText,
      detail: null,
    };
  }
  // Same count as the search's status line before PLT-7912: free-text hits, or the identifier
  // match when an ISIN or website resolved a company the text search missed (PLT-4775).
  const textResultCount = typeof data?.resultCount === 'number' ? data.resultCount : undefined;
  const identifierMatch = data?.identifierMatch as
    | { ambiguous?: boolean; teaser?: boolean; companyName?: string }
    | undefined;
  const identifierMatchCount =
    identifierMatch && identifierMatch.teaser !== true ? (identifierMatch.ambiguous === true ? 2 : 1) : 0;
  const resultCount = identifierMatchCount > 0 ? Math.max(textResultCount ?? 0, identifierMatchCount) : textResultCount;
  const matchedName = identifierMatchCount === 1 ? (identifierMatch?.companyName ?? null) : null;
  const label =
    resultCount === 0
      ? 'Found no matching company'
      : resultCount === 1
        ? 'Found company'
        : resultCount != null
          ? 'Found companies'
          : 'Searched companies';
  return { icon: 'search', label, subject: matchedName ?? query, state: 'done', liveText, detail: null };
}

/**
 * A call with no result is running until the run ends; after that nothing more is coming for it
 * (the user stopped the turn, or the stream broke), so it reads as not done.
 */
function stepState(part: TrailPart, data: Record<string, unknown> | undefined, runEnded: boolean): StepState {
  if (part.result === undefined) return runEnded ? 'failed' : 'running';
  return isFailedResult(data) ? 'failed' : 'done';
}

/**
 * Describe one tool call as a trail step. `companyName` on a result joins the subject, after the
 * call's own context ("Climate change mitigation · Nokia"), as it does on the card titles.
 */
function describeTrailStep(part: TrailPart, runEnded: boolean): TrailStep {
  const data = resultData(part.result);
  const state = stepState(part, data, runEnded);
  if (part.toolName === 'search') {
    return { toolCallId: part.toolCallId, toolName: part.toolName, ...describeSearch(part, data, state) };
  }

  const copy =
    part.toolName === 'text_editor_code_execution'
      ? fileStepCopy(part.args)
      : (STEP_COPY[part.toolName] ?? genericCopy(part.toolName));
  const subject =
    [
      copy.subject?.(part.args, data) ?? null,
      part.toolName === 'compare_releases' ? null : dataString(data, 'companyName'),
    ]
      .filter(Boolean)
      .join(' · ') || null;
  const label =
    state === 'running'
      ? copy.running
      : state === 'failed'
        ? copy.failed
        : ((data && copy.doneFrom?.(data)) ?? copy.done);
  const liveText = copy.live?.(part.args) ?? (subject ? `${copy.running}: ${subject}…` : `${copy.running}…`);
  // A reopened chat remembers only the call, so its row has nothing to open.
  const opens = state !== 'running' && !isTrailOnlyResult(part.result);
  // A background read opens its card from the row; every other card stays in the message.
  const hasCard = part.args.quiet === true && appNameOf(part.result) !== null;
  // Code shows what it ran and what it printed whether or not it failed; sources only once found.
  const opensDetail = state === 'done' || copy.detail === 'code';
  return {
    toolCallId: part.toolCallId,
    toolName: part.toolName,
    icon: copy.icon,
    label,
    subject,
    state,
    liveText,
    detail: !opens ? null : hasCard ? 'card' : opensDetail ? (copy.detail ?? null) : null,
  };
}

/** The MCP app a result renders as, when it renders as one. */
export function appNameOf(result: unknown): string | null {
  if (!result || typeof result !== 'object') return null;
  const appName = (result as { _ui?: { appName?: unknown } })._ui?.appName;
  return typeof appName === 'string' && appName.length > 0 ? appName : null;
}

/** How long a tool's step must read as running once the trail saw it running; 0 for most tools. */
export function minRunningMs(toolName: string): number {
  return STEP_COPY[toolName]?.minRunningMs ?? 0;
}

/**
 * The thinking blocks of a turn as one row. While a block is still streaming the row reads
 * "Thinking"; a block the run ended inside of reads as finished, since nothing more is coming for
 * it. A turn whose thinking left no text has no row, unless the text was hidden (Anthropic returns
 * redacted blocks without it), in which case the row says so.
 */
function describeReasoning(
  parts: readonly TrailPart[],
  runEnded: boolean,
): { step: TrailStep | null; text: string | null } {
  if (parts.length === 0) return { step: null, text: null };
  const [first] = parts;
  const thinking = !runEnded && parts.some(part => part.result === undefined);
  const texts = parts
    .map(part => (typeof part.args.text === 'string' ? part.args.text.trim() : ''))
    .filter(text => text.length > 0);
  const hidden = parts.some(part => part.args.redacted === true);
  if (!thinking && texts.length === 0 && !hidden) return { step: null, text: null };
  return {
    step: {
      toolCallId: first.toolCallId,
      toolName: REASONING_TOOL_NAME,
      icon: 'reasoning',
      label: thinking ? 'Thinking' : 'Reasoning',
      subject: null,
      state: thinking ? 'running' : 'done',
      liveText: 'Thinking…',
      detail: texts.length > 0 || hidden ? 'reasoning' : null,
    },
    text: texts.length > 0 ? texts.join('\n\n') : null,
  };
}

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

/**
 * The settled line: the first two actions in plain words, in the order they ran, then how many
 * more steps there were. A company search or a page fetch is named only when fewer than two other
 * actions ran: "Found company" says less about a turn than what was read about the company. One
 * step is the row itself, so its subject shows too (the caller renders it).
 */
function summariseTrail(steps: readonly TrailStep[]): string {
  if (steps.length === 0) return '';
  const [only] = steps;
  if (steps.length === 1) return only.label;
  const distinctLabels = (candidates: readonly TrailStep[]) => [...new Set(candidates.map(step => step.label))];
  const main = distinctLabels(steps.filter(step => !LESSER_STEPS.has(step.toolName)));
  const lesser = distinctLabels(steps.filter(step => LESSER_STEPS.has(step.toolName)));
  const chosen = new Set([...main, ...lesser.filter(label => !main.includes(label))].slice(0, 2));
  const named = distinctLabels(steps).filter(label => chosen.has(label));
  const more = steps.length - named.length;
  const [first, second] = named;
  if (!first) return '';
  if (!second) return `${first} and ${more} more`;
  return more > 0 ? `${first}, ${lowerFirst(second)} and ${more} more` : `${first} and ${lowerFirst(second)}`;
}

export interface Trail {
  /** Every row, in the order the turn ran them, the reasoning row first. */
  steps: TrailStep[];
  /** The reasoning row's text; null when there is no row or the text was hidden. */
  reasoningText: string | null;
  /** The step the turn is on now, the last one still waiting; null when none is. */
  current: TrailStep | null;
  /** The settled line. Empty when the turn used no tools: thinking alone draws no line once done. */
  summary: string;
  /** How many tool steps the trail has, not counting the reasoning row. */
  toolStepCount: number;
}

/**
 * The trail of one assistant turn from its trail parts in message order (`isTrailPart`).
 * `runEnded` is whether the message has stopped running; `held` lists steps that have their result
 * but must still read as running (`minRunningMs`).
 */
export function buildTrail(
  parts: readonly TrailPart[],
  { runEnded, held }: { runEnded: boolean; held?: ReadonlySet<string> },
): Trail {
  const effective =
    held && held.size > 0 && !runEnded
      ? parts.map(part => (held.has(part.toolCallId) ? { ...part, result: undefined } : part))
      : parts;
  const reasoning = describeReasoning(
    effective.filter(part => part.toolName === REASONING_TOOL_NAME),
    runEnded,
  );
  const toolSteps = effective
    .filter(part => part.toolName !== REASONING_TOOL_NAME)
    .map(part => describeTrailStep(part, runEnded));
  const byId = new Map(toolSteps.map(step => [step.toolCallId, step]));

  let current: TrailStep | null = null;
  if (!runEnded) {
    for (const part of effective) {
      if (part.result !== undefined) continue;
      current = part.toolName === REASONING_TOOL_NAME ? reasoning.step : (byId.get(part.toolCallId) ?? null);
    }
  }

  return {
    steps: reasoning.step ? [reasoning.step, ...toolSteps] : toolSteps,
    reasoningText: reasoning.text,
    current,
    summary: summariseTrail(toolSteps),
    toolStepCount: toolSteps.length,
  };
}
